/**
 * Route Traffic Analyzer Service
 *
 * Handles traffic analysis and visualization for routes including:
 * - Traffic severity calculation
 * - Traffic color coding
 * - Route comparison based on traffic
 * - Traffic data processing
 */

import {getTrafficSeverity, getTrafficColor, getTrafficDelay, type TrafficSeverity} from '@/utils/traffic';
import type {Route} from '@/types/route';

export interface TrafficAnalysis {
  severity: TrafficSeverity;
  color: string;
  description: string;
  delayMinutes: number;
}

export interface RouteTrafficComparison {
  primaryRoute: TrafficAnalysis;
  alternativeRoutes: Array<{
    route: Route;
    traffic: TrafficAnalysis;
    timeDifference: number;
    trafficAdvantage: string;
  }>;
  recommendation: string;
}

export class RouteTrafficAnalyzer {
    /**
   * Traffic severity thresholds
   */
    private static readonly TRAFFIC_THRESHOLDS = {
        LIGHT: 0.1,
        MODERATE: 0.25,
        HEAVY: 0.5,
        SEVERE: 0.75,
    };

    /**
   * Analyze traffic conditions for a single route
   * @param {Route} route - The route to analyze
   * @return {TrafficAnalysis} The traffic analysis for the route
   */
    static analyzeRouteTraffic(route: Route): TrafficAnalysis {
        const severity = getTrafficSeverity(route.duration, route.staticDuration || route.duration);
        const color = getTrafficColor(severity);

        return {
            severity,
            color,
            description: this.getTrafficDescription(severity),
            delayMinutes: getTrafficDelay(route.duration, route.staticDuration || route.duration),
        };
    }

    /**
   * Get traffic color for a route
   * @param {Route} route - The route to get traffic color for
   * @return {string} The traffic color code
   */
    static getRouteTrafficColor(route: Route): string {
        const severity = getTrafficSeverity(route.duration, route.staticDuration || route.duration);
        return getTrafficColor(severity);
    }

    /**
   * Compare traffic conditions across multiple routes
   * @param {Route} primaryRoute - The primary route to compare against
   * @param {Route[]} alternativeRoutes - Alternative routes to compare
   * @return {RouteTrafficComparison} Comparison results
   */
    static compareRouteTraffic(primaryRoute: Route, alternativeRoutes: Route[]): RouteTrafficComparison {
        const primaryTraffic = this.analyzeRouteTraffic(primaryRoute);

        const alternativeAnalyses = alternativeRoutes.map((route) => {
            const traffic = this.analyzeRouteTraffic(route);
            const primaryDuration = this.parseDuration(primaryRoute.duration);
            const altDuration = this.parseDuration(route.duration);
            const timeDifference = altDuration - primaryDuration;

            return {
                route,
                traffic,
                timeDifference,
                trafficAdvantage: this.getRouteAdvantage(primaryTraffic, traffic, timeDifference),
            };
        });

        return {
            primaryRoute: primaryTraffic,
            alternativeRoutes: alternativeAnalyses,
            recommendation: this.generateTrafficRecommendation(primaryTraffic, alternativeAnalyses),
        };
    }

    /**
   * Get a human-readable description of traffic severity
   * @param {TrafficSeverity} severity - The traffic severity level
   * @return {string} Human-readable description
   */
    static getTrafficDescription(severity: TrafficSeverity): string {
        switch (severity) {
            case 'none':
                return 'Clear roads';
            case 'light':
                return 'Light traffic';
            case 'moderate':
                return 'Moderate traffic';
            case 'heavy':
                return 'Heavy traffic';
            default:
                return 'Traffic conditions';
        }
    }

    /**
   * Convert traffic severity to numeric value for comparisons
   * @param {TrafficSeverity} severity - The traffic severity level
   * @return {number} Numeric representation of severity
   */
    private static severityToNumber(severity: TrafficSeverity): number {
        switch (severity) {
            case 'none': return 0;
            case 'light': return 0.25;
            case 'moderate': return 0.5;
            case 'heavy': return 0.75;
            default: return 0;
        }
    }

    /**
   * Parse duration string to seconds
   * @param {string} duration - Duration string to parse
   * @return {number} Duration in seconds
   */
    private static parseDuration(duration: string): number {
        return parseInt(duration.replace('s', ''));
    }

    /**
   * Get route advantage description based on traffic comparison
   * @param {TrafficAnalysis} primaryTraffic - Traffic analysis for primary route
   * @param {TrafficAnalysis} alternativeTraffic - Traffic analysis for alternative route
   * @param {number} timeDifference - Time difference in seconds
   * @return {string} Route advantage description
   */
    private static getRouteAdvantage(primaryTraffic: TrafficAnalysis, alternativeTraffic: TrafficAnalysis, timeDifference: number): string {
    // If alternative route is significantly faster despite traffic
        if (timeDifference < -300) { // 5+ minutes faster
            return 'Faster route';
        }

        // If alternative route has much better traffic conditions
        const primarySeverityNum = this.severityToNumber(primaryTraffic.severity);
        const altSeverityNum = this.severityToNumber(alternativeTraffic.severity);
        if (altSeverityNum < primarySeverityNum - 0.2) {
            return 'Less traffic';
        }

        // If alternative route is shorter (but might be slower due to traffic)
        if (timeDifference > 0 && timeDifference < 600) { // Up to 10 minutes slower
            return 'Shorter route';
        }

        // If routes are very similar
        if (Math.abs(timeDifference) < 120) { // Within 2 minutes
            return 'Similar time';
        }

        return '';
    }

    /**
   * Generate traffic-based recommendation
   * @param {TrafficAnalysis} primaryTraffic - Traffic analysis for primary route
   * @param {Array<any>} alternatives - Alternative route analyses
   * @return {string} Traffic recommendation text
   */
    private static generateTrafficRecommendation(primaryTraffic: TrafficAnalysis, alternatives: Array<any>): string {
    // Find the best alternative based on traffic
        const bestAlternative = alternatives.reduce((best, current) => {
            if (!best) return current;

            // Prefer routes with less traffic delay
            const bestScore = best.traffic.delayMinutes + (best.timeDifference / 60);
            const currentScore = current.traffic.delayMinutes + (current.timeDifference / 60);

            return currentScore < bestScore ? current : best;
        }, null);

        if (!bestAlternative) {
            return 'Current route is optimal';
        }

        const timeSavings = Math.round(bestAlternative.timeDifference / -60);
        const trafficImprovement = primaryTraffic.delayMinutes - bestAlternative.traffic.delayMinutes;

        if (timeSavings > 5) {
            return `Alternative route saves ${timeSavings} minutes`;
        }

        if (trafficImprovement > 3) {
            return `Alternative route has ${trafficImprovement} minutes less traffic delay`;
        }

        return 'Current route is recommended';
    }

    /**
   * Check if traffic conditions warrant showing alternatives
   * @param {Route} route - The route to check
   * @return {boolean} Whether to show traffic alternatives
   */
    static shouldShowTrafficAlternatives(route: Route): boolean {
        const traffic = this.analyzeRouteTraffic(route);
        const severityNum = this.severityToNumber(traffic.severity);
        return severityNum > this.TRAFFIC_THRESHOLDS.MODERATE || traffic.delayMinutes > 5;
    }

    /**
   * Get traffic severity category
   * @param {Route} route - The route to analyze
   * @return {TrafficSeverity} The traffic severity category
   */
    static getTrafficSeverityCategory(route: Route): TrafficSeverity {
        return getTrafficSeverity(route.duration, route.staticDuration || route.duration);
    }

    /**
   * Format traffic delay for display
   * @param {number} delayMinutes - Delay in minutes
   * @return {string} Formatted delay string
   */
    static formatTrafficDelay(delayMinutes: number): string {
        if (delayMinutes < 1) return 'No delay';
        if (delayMinutes < 60) return `${delayMinutes} min delay`;

        const hours = Math.floor(delayMinutes / 60);
        const minutes = delayMinutes % 60;

        if (minutes === 0) return `${hours} hr delay`;
        return `${hours} hr ${minutes} min delay`;
    }

    /**
   * Get traffic icon based on severity
   * @param {Route} route - The route to get icon for
   * @return {string} Traffic icon emoji
   */
    static getTrafficIcon(route: Route): string {
        const category = this.getTrafficSeverityCategory(route);
        const icons: Record<TrafficSeverity, string> = {
            none: '🟢',
            light: '🟢',
            moderate: '🟡',
            heavy: '🔴',
        };
        return icons[category];
    }
}
